import React, { useState } from 'react';
import { DashboardData, getHeaders, JobResponse } from '../../util';
import Table from '../../../../components/table/BkTable';
import CancelButton from '../CancelButton';
import { Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { apiInstance } from '../../../../api/apiInstance';
import { Column } from '../../../../utils/constants/table-constants';
import DetailedJobView from '../../../../components/modals/DetailedJobView';
import JobStatus from './JobStatus';
import { downloadFile } from 'src/utils/utility-functions';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import ServiceBadge from 'src/components/service-badge/ServiceBadge';

interface ReportsTableProps {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  showPagination: boolean;
  data: DashboardData[];
  showUserColumn?: boolean;
  updateData: (data: any) => void;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const ReportsTable: React.FC<ReportsTableProps> = ({
  showPagination,
  data,
  updateData,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  showUserColumn = false,
}) => {
  const [fetchedJob, setFetchedJob] = useState<JobResponse | null>(null);
  const [showJobDialog, setShowJobDialog] = useState<boolean>(false);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const fetchSingleJob = async (id: string) => {
    try {
      const response = await apiInstance.get(`/job/item/${id}`);
      setFetchedJob(response.data.data);
    } catch (err) {
      setShowJobDialog(false);
    }
  };

  const onJobOpen = (jobId: string) => {
    fetchSingleJob(jobId);
    setShowJobDialog(true);
  };

  const onJobClose = () => {
    setShowJobDialog(false);
    setFetchedJob(null);
  };

  const handleCancelClick = async (id: string) => {
    try {
      await apiInstance.post(`/job/item/${id}/cancel`);
      const updatedData = data.map((item) => (item.id === id ? { ...item, jobStatus: 'cancelled' } : item));
      updateData(updatedData);
      setShowSnackbarMsg({ msg: 'Job canceled successfully', type: 'success' });
    } catch (err: any) {
      setShowSnackbarMsg({ msg: 'Could not cancel job', type: 'error' });
    }
  };

  const onDownloadCertificate = async (trackingNumber: string, id: string) => {
    try {
      await downloadFile(`/job/item/${id}/certificate/download`, `${trackingNumber}_certificate`);
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.message, type: 'error' });
    }
  };

  const onRequestCertificate = async (id: string) => {
    try {
      await apiInstance.post(`/job/item/${id}/certificate/request`);
      const updatedData = data.map((item) => (item.id === id ? { ...item, action: 'requested' } : item));
      updateData(updatedData);
      setShowSnackbarMsg({ msg: 'Request sent. Please allow at least 12 hours as we pull Certificate from Archive.', type: 'success' });
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.response.data.message, type: 'error' });
    }
  };

  const renderAction = (
    action: 'cancel' | 'download' | 'request' | 'requested' | 'disabled',
    trackingNumber: string,
    id: string,
    status: string,
  ) => {
    switch (action) {
      case 'disabled':
        return <CancelButton onClick={() => handleCancelClick(id)} disabled={true} />;
      case 'cancel':
        return <CancelButton onClick={() => handleCancelClick(id)} disabled={status === 'cancelled'} />;
      case 'download':
        return (
          <Button
            disabled={status !== 'completed'}
            onClick={() => onDownloadCertificate(trackingNumber, id)}
            sx={{
              color: 'black',
              border: '0.0625rem solid #ece9dc',
              borderRadius: '0rem',
              textTransform: 'inherit',
              paddingX: '0.625rem',
              alignSelf: 'center'
            }}
          >
            <DownloadIcon sx={{ marginRight: '0.3125rem', fontSize: '1rem' }} />
            Download
          </Button>
        );
      case 'request':
        return (
          <Button
            onClick={() => onRequestCertificate(id)}
            sx={{
              color: 'black',
              border: '0.0625rem solid #ece9dc',
              borderRadius: '0rem',
              textTransform: 'inherit',
              paddingX: '0.625rem',
            }}
          >
            <DownloadIcon sx={{ marginRight: '0.3125rem', fontSize: '1rem' }} />
            Request
          </Button>
        );
      case 'requested':
        return (
          <Button
            disabled={true}
            sx={{
              color: 'black',
              border: '0.0625rem solid #ece9dc',
              borderRadius: '0rem',
              textTransform: 'inherit',
              paddingX: '0.625rem',
            }}
          >
            <DownloadIcon sx={{ marginRight: '0.3125rem', fontSize: '1rem' }} />
            Requested
          </Button>
        );
      default:
        return action;
    }
  };

  const renderTracking = (trackingNumber: string, jobId: string) => {
    return (
      <p
        style={{
          textDecoration: 'underline',
          color: '#436eb2',
          cursor: 'pointer',
        }}
        onClick={() => onJobOpen(jobId)}
      >
        {trackingNumber}
      </p>
    );
  };

  const renderDebtorName = (row: any) => {
    if (row.jointDebtorName) {
      return `${row.debtorName} and ${row.jointDebtorName}`;
    }
    return row.debtorName;
  }

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'action':
        return renderAction(row.action, row.tracking, row.id, row.jobStatus);
      case 'status':
        return <JobStatus status={row.jobStatus} />;
      case 'tracking':
        return renderTracking(row.tracking, row.id);
      case 'debtorName':
        return renderDebtorName(row);
      case 'service':
        return <ServiceBadge service={row.service} />;
      default:
        return row[column.field];
    }
  };

  return (
    <div>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <Table
        columns={getHeaders(showUserColumn)}
        data={data}
        initialRowsPerPage={10}
        renderCell={renderCell}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        renderPagination={showPagination}
      />
      {showJobDialog && <DetailedJobView job={fetchedJob} open={showJobDialog} onClose={onJobClose} />}
    </div>
  );
};

export default ReportsTable;
